/*
 *   File : manage-sub-admin.jsx
 *   Author : https://evoqins.com
 *   Description : Manage Sub Admins and their roles/modules
 *   Version : 1.1.0 (Improved navigation sync)
 */

import React, { useEffect, useState } from "react";
import { CustomSearchInput } from "../../Components/FormElements";
import { PrimaryButton } from "../../Components/Cta";
import { DataTableComponent } from "../../Components/DataTable";
import Icon from "../../Components/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { Chip, CustomTab, EmptyScreen, Loader } from "../../Components/Other";
import { ConfirmationModal } from "../../Components/Modals";
import { APIService } from "../../Service";
import { toast } from "react-toastify";

const TAB_DATA = [
  { id: "subAdmins", label: "Sub Admins", name: "Sub Admins" },
  { id: "modules", label: "Modules", name: "Modules" },
];

export default function ManageSubAdmin() {
  const navigate = useNavigate();
  const location = useLocation();

  // derive tab directly from pathname (single source of truth)
  const currentTab = location.pathname.includes("/modules")
    ? "modules"
    : "subAdmins";

  const [loading, setLoading] = useState(false);
  const [delId, setDelId] = useState("");
  const [subAdmins, setSubAdmins] = useState([]);
  const [emptyScreen, setEmptyScreen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalRows, setTotalRows] = useState(0);
  const [editData, setEditData] = useState({});
  const [moduleData, setModuleData] = useState([]);
  const [blockStatus, setBlockStatus] = useState("");

  // fetch sub admins on mount and when pagination changes
  useEffect(() => {
    if (currentTab !== "subAdmins") return;
    // if (searchValue.trim() !== "") return;
    setLoading(true);
    _getSubAdmins(currentPage, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, currentPage, rowsPerPage, searchValue]);

  // fetch module roles when tab is "modules"
  useEffect(() => {
    if (currentTab === "modules") {
      setLoading(true);
      _getRoleList();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [currentTab]);

  // handle edit navigation
  useEffect(() => {
    sessionStorage.removeItem("editingSubAdmin");
    if (editData.admin_id || editData.id) {
      sessionStorage.setItem("editingSubAdmin", JSON.stringify(editData));
      if (currentTab === "subAdmins") {
        navigate(`/manage-sub-admin/edit-sub-admin/${editData.admin_id}`);
      } else {
        navigate(`/manage-sub-admin/edit-modules/${editData.id}`);
      }
    }
  }, [editData, navigate, currentTab]);

  const _handleTabChange = (tabId) => {
    if (tabId === "modules") navigate("/manage-sub-admin/modules");
    else navigate("/manage-sub-admin");
  };

  const _handleRowClick = (row) => {
    navigate(`/manage-sub-admin/admin-detail/${row.admin_id}`);
  };

  // service to get sub admins
  const _getSubAdmins = (page = 1, size = 15) => {
    const payload = { query: searchValue, page_num: page, page_size: size };
    APIService(true, "sub-admin/list", payload)
      .then((response) => {
        if (response.status_code === 200) {
          const respData = response.data || {};
          const list = respData.data || [];
          setSubAdmins(list);
          setEmptyScreen(list.length === 0);
          setTotalRows(respData.total_count ?? list.length);
          setRowsPerPage(respData.page_size ?? size);
        } else {
          setSubAdmins([]);
          setEmptyScreen(true);
        }
      })
      .catch(() => {
        setSubAdmins([]);
        setEmptyScreen(true);
      })
      .finally(() => setLoading(false));
  };

  // service to get role list
  const _getRoleList = () => {
    APIService(true, "sub-admin/role/list", { query: null })
      .then((response) => {
        if (response.status_code === 200) {
          setModuleData(response.data.data);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  // block/unblock user
  const _blockUser = () => {
    APIService(true, "sub-admin/block", { id: delId })
      .then((response) => {
        if (response.status_code === 200) {
          toast.success(response.message);
          _getSubAdmins();
          setShowModal(false);
        }
      })
      .catch(console.error)
      .finally(() => {
        setBlockStatus("");
        setLoading(false);
      });
  };

  // delete role
  const _roleDelete = () => {
    const payload = { role_id: delId };
    APIService(true, "sub-admin/roles/delete", payload)
      .then((response) => {
        if (response.status_code === 200) {
          toast.success(response.message);
          _getRoleList();
          setShowModal(false);
        }
        if (response.status_code === 400) {
          toast.error(response.message);
          setShowModal(false);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error(error.message);
      })
      .finally(() => {
        setBlockStatus("");
        setLoading(false);
      });
  };

  // Columns for Sub Admins
  const COLUMNS = [
    {
      name: "Name",
      width: "199px",
      selector: (row) => row.username || row.name || "-",
      sortable: true,
    },
    {
      name: "Email",
      width: "250px",
      selector: (row) => row.email || "-",
      sortable: true,
    },
    {
      name: "Phone Number",
      width: "172px",
      selector: (row) => row.mobile_number || "-",
      sortable: true,
    },
    {
      name: "Role",
      width: "152px",
      selector: (row) => row.role || "-",
      sortable: true,
    },
    {
      name: "Modules",
      width: "350px",
      cell: (row) => (
        <div className="d-flex gap-2 flex-wrap">
          {(row.modules || []).map((mod, index) => (
            <Chip
              key={index}
              borderColor="e-border-1px-blue"
              fontColor="e-text-blue-violet"
              fontWeight="e-font-weight-500"
              fontSize="e-font-14"
              label={mod?.name ?? mod}
            />
          ))}
        </div>
      ),
    },
    {
      name: "Status",
      width: "100px",
      cell: (row) => {
        if (row.is_blocked) {
          return (
            <Chip
              label="Blocked"
              borderColor="e-border-1px-ruby"
              bgColor="e-bg-pale-ruby"
              fontColor="e-text-ruby"
              fontWeight="e-font-weight-500"
            />
          );
        }
        if (
          row.is_active ||
          (row.block_status || "").toLowerCase() === "unblocked"
        ) {
          return (
            <Chip
              label="Active"
              borderColor="e-border-1px-green"
              bgColor="e-bg-pale-green"
              fontColor="e-text-green"
              fontWeight="e-font-weight-500"
            />
          );
        }
        return (
          <Chip
            label="Inactive"
            borderColor="e-border-1px-ruby"
            bgColor="e-bg-pale-ruby"
            fontColor="e-text-ruby"
            fontWeight="e-font-weight-500"
          />
        );
      },
    },
    {
      name: "Action",
      width: "300px",
      cell: (row) => (
        <div className="d-flex justify-content-between align-items-center">
          <PrimaryButton
            name="Edit"
            icon="edit"
            onPress={() => setEditData(row)}
            ClassName="bg-transparent e-text-blue-violet e-font-weight-400 e-font-14"
          />
          {row.is_blocked ? (
            <PrimaryButton
              name="Unblock User"
              icon="unblock"
              size="22px"
              onPress={() => {
                setDelId(row.admin_id);
                setBlockStatus("Unblock");
                setShowModal(true);
              }}
              ClassName="bg-transparent e-text-gray e-font-weight-400 e-font-14"
            />
          ) : (
            <PrimaryButton
              name="Block User"
              icon="block"
              size="16px"
              onPress={() => {
                setDelId(row.admin_id);
                setBlockStatus("Block");
                setShowModal(true);
              }}
              ClassName="bg-transparent e-text-cg-red e-font-weight-400 e-font-14"
            />
          )}
        </div>
      ),
    },
  ];

  // Columns for Modules
  const COLUMNS_MODULES = [
    {
      name: "Role Name",
      width: "200px",
      selector: (row) => row.name || "-",
      sortable: true,
    },
    {
      name: "Modules",
      width: "350px",
      cell: (row) =>
        row.modules?.length ? (
          <div className="d-flex flex-wrap gap-2">
            {row.modules.map((mod, i) => (
              <Chip
                key={i}
                borderColor="e-border-1px-blue"
                fontColor="e-text-blue-violet"
                fontWeight="e-font-weight-500"
                fontSize="e-font-14"
                label={mod?.name ?? mod}
              />
            ))}
          </div>
        ) : (
          <span className="text-muted">No modules</span>
        ),
    },
    {
      name: "Action",
      width: "300px",
      cell: (row) => (
        <div className="d-flex justify-content-between align-items-center">
          <PrimaryButton
            name="Edit"
            icon="edit"
            onPress={() => setEditData(row)}
            ClassName="bg-transparent e-text-blue-violet e-font-weight-400 e-font-14"
          />
          <PrimaryButton
            name="Delete"
            icon="delete"
            onPress={() => {
              setDelId(row.id);
              setShowModal(true);
            }}
            ClassName="bg-transparent e-text-cg-red e-font-weight-400 e-font-14"
          />
        </div>
      ),
    },
  ];

  return (
    <div className="row px-3">
      <div className="col-12 py-4">
        {/* Tabs */}
        <div className="row">
          <div className="col-12">
            <div className="e-customer-tab d-flex flex-nowrap justify-content-between align-items-center e-border-1px-bottom-magnolia-light">
              <div className="d-flex column-gap-2">
                {TAB_DATA.map((data) => (
                  <CustomTab
                    key={data.id}
                    type={1}
                    borderRadius="border-radius-4"
                    tabData={data}
                    tabIndex={currentTab}
                    background="bg-white"
                    handleChange={() => _handleTabChange(data.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Search + Button */}
        <div className="row">
          <div className="offset-lg-7 offset-md-3 col-12 col-lg-5 col-md-6 col-sm-10">
            <div className="d-flex justify-content-end align-items-center column-gap-2 pt-2">
              {currentTab !== "modules" && (
                <div className="flex-grow-1">
                  <CustomSearchInput
                    type="text"
                    id="search"
                    placeholder="Search with name"
                    className="w-100 e-search-input-padding"
                    value={searchValue}
                    handleChange={(val) => setSearchValue(val)}
                    onKeyPress={_getSubAdmins}
                  />
                </div>
              )}
              <PrimaryButton
                name={
                  currentTab === "subAdmins"
                    ? "Add new sub admin"
                    : "Create new role"
                }
                ClassName="e-bg-blue-violet rounded-pill text-white e-font-14 e-primary-font-poppins e-font-weight-500 py-2"
                onPress={() =>
                  currentTab === "subAdmins"
                    ? navigate("/manage-sub-admin/add-sub-admin")
                    : navigate("/manage-sub-admin/add-modules")
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="row w-100 d-flex ms-1">
        {loading ? (
          <div className="d-flex min-vh-100 justify-content-center align-items-center">
            <Loader width={30} />
          </div>
        ) : emptyScreen ? (
          <EmptyScreen width={150} message="No Records To Show" />
        ) : currentTab === "subAdmins" ? (
          <DataTableComponent
            columns={COLUMNS}
            data={subAdmins}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={rowsPerPage}
            currentPage={currentPage}
            onRowClicked={_handleRowClick}
            sortIcon={<Icon icon="sort_col" size="20px" />}
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(newPerPage, page) => {
              setRowsPerPage(newPerPage);
              setCurrentPage(page || 1);
            }}
            className="e-customer-investment-table"
          />
        ) : (
          <DataTableComponent
            columns={COLUMNS_MODULES}
            data={moduleData}
            sortIcon={<Icon icon="sort_col" size="20px" />}
            className="e-sub-admin-module-table"
          />
        )}
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <ConfirmationModal
          title={
            currentTab === "subAdmins"
              ? `Do You want to ${blockStatus} this admin`
              : "Do you want to Delete this role"
          }
          message={
            blockStatus.trim() === ""
              ? "Are you sure you want to delete this?"
              : `Are you sure you want to ${blockStatus} this admin?`
          }
          close={() => {
            setShowModal(false);
            setBlockStatus("");
          }}
          onClick={currentTab === "subAdmins" ? _blockUser : _roleDelete}
        />
      )}
    </div>
  );
}
